/**
 * @file 本体组成元素列表面板，显示对象和过程节点
 */

import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Tabs, Empty, Typography } from 'antd';
import { cy } from './DiagramCanvas';

const { Text } = Typography;

const ITEM_HEIGHT = 36;
const OVERSCAN_COUNT = 5;

interface NodeListPanelProps {
  timestamp?: Date;
}

interface SimpleNode {
  id: string;
  label: string;
  type: string;
}

// 获取节点颜色
const getNodeColor = (type: string): string => {
  if (type === 'object') return '#70E483';
  if (type === 'process') return '#3BC3FF';
  return '#BFBFBF';
};

// 虚拟列表组件
const VirtualList: React.FC<{
  items: SimpleNode[];
  renderItem: (node: SimpleNode, index: number) => React.ReactNode;
  height: number;
  className?: string;
}> = ({ items, renderItem, height, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = items.length * ITEM_HEIGHT;

  const visibleRange = useMemo(() => {
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN_COUNT
    );
    const visibleCount = Math.ceil(height / ITEM_HEIGHT) + 2 * OVERSCAN_COUNT;
    const endIndex = Math.min(items.length - 1, startIndex + visibleCount);
    return { startIndex, endIndex };
  }, [scrollTop, height, items.length]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const visibleItems = useMemo(() => {
    const result: React.ReactNode[] = [];
    for (let i = visibleRange.startIndex; i <= visibleRange.endIndex; i++) {
      const item = items[i];
      if (item) {
        result.push(
          <div
            key={item.id}
            style={{
              position: 'absolute',
              top: i * ITEM_HEIGHT,
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
            }}
          >
            {renderItem(item, i)}
          </div>
        );
      }
    }
    return result;
  }, [items, visibleRange, renderItem]);

  if (items.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height,
        overflow: 'auto',
        position: 'relative',
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems}
      </div>
    </div>
  );
};

const NodeListPanel: React.FC<NodeListPanelProps> = ({ timestamp }) => {
  const [activeTab, setActiveTab] = useState('objects');
  const [refreshKey, setRefreshKey] = useState(0);
  const [cyReady, setCyReady] = useState(false);

  // 轮询等待 cytoscape 实例就绪（因 cy 为模块变量，需手动触发一次重渲染）
  useEffect(() => {
    if (cy) {
      setCyReady(true);
      return;
    }
    const timer = setInterval(() => {
      if (cy) {
        setCyReady(true);
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // 监听图数据变化，触发刷新
  useEffect(() => {
    if (!cyReady || !cy) return;

    const rerender = () => setRefreshKey(key => key + 1);
    // 初次触发一次
    rerender();
    cy.on('add remove data style', rerender);
    cy.on('layoutstop', rerender);
    return () => {
      cy.off('add remove data style', rerender);
      cy.off('layoutstop', rerender);
    };
  }, [cyReady]);

  // 获取节点显示名称
  const getDisplayName = useCallback((node: any): string => {
    const data = node.data ? node.data() : {};
    return data.label || data.name || data.id || '未命名节点';
  }, []);

  // 计算当前可展示的节点（过滤 display = none）
  const { objectNodes, processNodes } = useMemo(() => {
    if (!cyReady || !cy) {
      return {
        objectNodes: [] as SimpleNode[],
        processNodes: [] as SimpleNode[],
      };
    }
    const visibleNodes = cy
      .nodes()
      .filter(node => node.data('display') !== 'none');

    const objects = visibleNodes
      .filter(node => node.data('type') === 'object')
      .map(node => ({
        id: node.id(),
        label: getDisplayName(node),
        type: 'object',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const processes = visibleNodes
      .filter(node => node.data('type') === 'process')
      .map(node => ({
        id: node.id(),
        label: getDisplayName(node),
        type: 'process',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { objectNodes: objects, processNodes: processes };
  }, [cyReady, refreshKey, timestamp, getDisplayName]);

  // 点击节点时定位到该节点并高亮
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      if (!cyReady || !cy) return;
      try {
        const node = cy.getElementById(nodeId);
        if (node && node.length > 0) {
          cy.elements().unselect();
          node.select();
          cy.animate({
            center: { eles: node },
            duration: 300,
            complete: () => {
              // (node as any).flashClass('bgRed', 1000);
              // node.select();
              // console.log('node selected', node);
            },
          });
          // 触发关闭节点编辑侧边栏（列表点击视为新的选择来源）
          if (
            typeof window !== 'undefined' &&
            (window as any).__closeEditorIfNotNode
          ) {
            (window as any).__closeEditorIfNotNode(null);
          }
        }
      } catch (error) {
        console.warn('定位节点失败:', error);
      }
    },
    [cyReady]
  );

  // 渲染节点列表项
  const renderNodeItem = useCallback(
    (node: SimpleNode) => (
      <div
        key={node.id}
        className="node-list-item"
        onClick={() => handleNodeClick(node.id)}
        style={{ height: ITEM_HEIGHT }}
      >
        <div className="node-item-content">
          <div
            className="node-type-indicator"
            style={{ backgroundColor: getNodeColor(node.type) }}
          />
          <Text className="node-name" title={node.label}>
            {node.label}
          </Text>
        </div>
      </div>
    ),
    [handleNodeClick]
  );

  const tabItems = useMemo(
    () => [
      {
        key: 'objects',
        label: <span>对象 ({objectNodes.length})</span>,
        children:
          objectNodes.length > 0 ? (
            <VirtualList
              items={objectNodes}
              renderItem={renderNodeItem}
              height={400}
              className="node-list"
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无对象节点"
              className="empty-state"
            />
          ),
      },
      {
        key: 'processes',
        label: <span>过程 ({processNodes.length})</span>,
        children:
          processNodes.length > 0 ? (
            <VirtualList
              items={processNodes}
              renderItem={renderNodeItem}
              height={400}
              className="node-list"
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无过程节点"
              className="empty-state"
            />
          ),
      },
    ],
    [objectNodes, processNodes, renderNodeItem]
  );

  return (
    <div className="node-list-panel">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="node-list-tabs"
        size="small"
        items={tabItems}
      />
    </div>
  );
};

export default NodeListPanel;
